import React, { useState } from 'react';
import axios from 'axios';

const BulkMessageSender = () => {
  const [message, setMessage] = useState('');
  const [webAppUrl, setWebAppUrl] = useState('');
  const [buttonTitle, setButtonTitle] = useState('');
  const [includeButtons, setIncludeButtons] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [searchTelegramId, setSearchTelegramId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [progress, setProgress] = useState(0); // Progress counter for live stats

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setLoadingAll(true);
    setError(null);
    setResults(null);
    setProgress(0); // Reset progress counter

    try {
      const response = await axios.post(`${SERVER_URL}/api/sendBulkMessage`, {
        message,
        webAppUrl: includeButtons ? webAppUrl : undefined,
        buttonTitle: includeButtons ? buttonTitle : undefined,
        includeButtons,
        imageUrl: imageUrl || undefined,
      }, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted); // Update progress counter
        }
      });

      setResults(response.data);
      setMessage('');
      setImageUrl('');
      if (!includeButtons) {
        setWebAppUrl('');
        setButtonTitle('');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send messages');
    } finally {
      setLoadingAll(false);
    }
  };

  const handleSearchUser = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/api/search-user`, {
        params: { telegramId: searchTelegramId }
      });

      setSelectedUser(response.data.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to search user');
    }
  };

  const handleSendToSelectedUser = async () => {
    if (!selectedUser || !message) {
      setError("Please select a user and provide a message");
      return;
    }
  
    if (includeButtons && (!webAppUrl || !buttonTitle)) {
      setError("Web App URL and Button Title are required when including buttons");
      return;
    }
  
    setLoadingSelected(true);
    setError(null);
  
    try {
      const response = await axios.post(`${SERVER_URL}/api/send-message`, {
        telegramId: selectedUser.telegramId,
        message,
        includeButtons,
        webAppUrl: includeButtons ? webAppUrl : undefined,
        buttonTitle: includeButtons ? buttonTitle : undefined,
        imageUrl: imageUrl || undefined,
      });
  
      setResults({
        totalUsers: 1,
        successCount: 1,
        failureCount: 0,
        failures: [],
        estimatedTimeSeconds: 0,
      });
  
      setMessage('');
      setImageUrl('');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send message to selected user');
    } finally {
      setLoadingSelected(false);
    }
  };

  return (
    <div className='w-full flex justify-start h-screen'>
      <div className="w-full p-4 sm:max-w-[70%]">
        <h2 className="text-2xl font-bold mb-4">Send Broadcast to Users</h2>

        {/* Search User by Telegram ID */}
        <div className="mb-4">
          <h3 className="text-xl font-bold mb-2">Search User by Telegram ID</h3>
          <div className="flex space-x-2">
            <input
              type="text"
              value={searchTelegramId}
              onChange={(e) => setSearchTelegramId(e.target.value)}
              placeholder="Enter Telegram ID..."
              className="bg-white/25 w-full placeholder:text-[#b9b9b9] text-[#e0e0e0] placeholder:text-[12px] text-[13px] placeholder:font-light h-[55px] border-none outline-none rounded-[10px] flex items-center px-2"
            />
            <button
              onClick={handleSearchUser}
              className="px-4 py-2 bg-blue-500 text-white rounded"
            >
              Search
            </button>
          </div>

          {selectedUser && (
            <div className="mt-4">
              <p>Selected User: {selectedUser.telegramId}</p>
            </div>
          )}
        </div>

        {/* Message Input */}
        <form onSubmit={handleSendMessage}>
          <div className="w-full">
            {/* Image URL Input */}
            <div className="mb-4">
              <label className="block mb-2">Image URL:</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="Enter image URL..."
                disabled={loadingAll || loadingSelected}
                className="bg-white/25 w-full placeholder:text-[#b9b9b9] text-[#e0e0e0] placeholder:text-[12px] text-[13px] placeholder:font-light h-[55px] border-none outline-none rounded-[10px] flex items-center px-2"
              />
            </div>

            <label className="block mb-2 mt-3">Broadcast Content:</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message to send to all users..."
              disabled={loadingAll || loadingSelected}
              rows={8}
              className="bg-white/25 w-full placeholder:text-[#b9b9b9] text-[#e0e0e0] placeholder:text-[12px] text-[13px] placeholder:font-light border-none outline-none rounded-[10px] flex items-center px-2 pt-3"
              required
            />

            <div className="mb-4">
              <label className="mb-4 mt-4 flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={includeButtons}
                  onChange={(e) => setIncludeButtons(e.target.checked)}
                  className="form-checkbox"
                />
                <span>Include buttons in Broadcast</span>
              </label>
            </div>

            {includeButtons && (
              <div className='w-full flex flex-col space-y-2'>
                <div className="w-full">
                  <label className="block mb-2">Web App URL:</label>
                  <input
                    type="url"
                    value={webAppUrl}
                    onChange={(e) => setWebAppUrl(e.target.value)}
                    placeholder="Enter your web app URL..."
                    disabled={loadingAll || loadingSelected}
                    className="bg-white/25 w-full placeholder:text-[#b9b9b9] text-[#e0e0e0] placeholder:text-[12px] text-[13px] placeholder:font-light h-[55px] border-none outline-none rounded-[10px] flex items-center px-2"
                    required={includeButtons}
                  />
                </div>
                <div className="w-full">
                  <label className="block mb-2">Button Title:</label>
                  <input
                    type="text"
                    value={buttonTitle}
                    onChange={(e) => setButtonTitle(e.target.value)}
                    placeholder="Enter button title.."
                    disabled={loadingAll || loadingSelected}
                    className="bg-white/25 w-full placeholder:text-[#b9b9b9] text-[#e0e0e0] placeholder:text-[12px] text-[13px] placeholder:font-light h-[55px] border-none outline-none rounded-[10px] flex items-center px-2"
                    required={includeButtons}
                  />
                </div>
              </div>
            )}

          </div>

          {/* Buttons at the bottom */}
          <div className="flex space-x-2 mt-4">
            <button
              type="submit"
              disabled={loadingAll || !message.trim() || (includeButtons && !webAppUrl.trim())}
              className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-400"
            >
              {loadingAll ? 'Sending...' : 'Send to All Users'}
            </button>

            <button
              onClick={handleSendToSelectedUser}
              disabled={loadingSelected || !selectedUser || !message.trim()}
              className="px-4 py-2 bg-green-500 text-white rounded disabled:bg-gray-400"
            >
              {loadingSelected ? 'Sending...' : 'Send to Selected User'}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}

        {results && (
          <div className="mt-4 p-4 border rounded-[16px] border-purple-400/40 bg-cards">
            <h3 className="font-bold mb-2">Results:</h3>
            <div className="space-y-2">
              <p>Total Users: {results.totalUsers}</p>
              <p className="text-green-600">Successfully Sent: {results.successCount}</p>
              <p className="text-red-600">Failed: {results.failureCount}</p>
              <p className="text-blue-600">
                Estimated Time: {Math.floor(results.estimatedTimeSeconds / 60)} minutes and{' '}
                {results.estimatedTimeSeconds % 60} seconds
              </p>
              {results.failures?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-bold mb-2">Failed Messages:</h4>
                  <div className="max-h-40 overflow-y-auto">
                    {results.failures.map((failure, index) => (
                      <div key={index} className="text-sm text-red-600">
                        User ID: {failure.telegramId} - {failure.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {loadingAll && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
            </div>
            <p className="text-sm text-gray-500 mt-2">Progress: {progress}%</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkMessageSender;